<template>
  <div class="glass-page" :class="[themeClass, { 'glass-page--centered': centered }]">
    <!-- Animated Background -->
    <AnimatedBackground :theme="theme" />
    
    <!-- Loading Overlay -->
    <transition name="fade">
      <div v-if="loading" class="loading-overlay">
        <div class="loader-glass">
          <div class="loader-spinner"></div>
          <span>{{ loadingText }}</span>
        </div>
      </div>
    </transition>
    
    <!-- Page Content -->
    <div class="glass-page__content" :class="[`glass-page__content--${maxWidth}`]">
      <!-- Page Header -->
      <div v-if="title || $slots.header" class="glass-page__header">
        <slot name="header">
          <div class="glass-page__header-content">
            <button v-if="showBack" class="back-btn" @click="goBack">
              <i class="fas fa-arrow-left"></i>
              <span>Retour</span>
            </button>
            <div class="glass-page__title-section">
              <h1 v-if="title" class="glass-page__title">{{ title }}</h1>
              <p v-if="subtitle" class="glass-page__subtitle">{{ subtitle }}</p>
            </div>
            <div v-if="$slots.actions" class="glass-page__actions">
              <slot name="actions"></slot>
            </div>
          </div>
        </slot>
      </div>
      
      <!-- Main Content -->
      <main class="glass-page__main">
        <slot></slot>
      </main>
      
      <!-- Page Footer -->
      <div v-if="$slots.footer" class="glass-page__footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import AnimatedBackground from './AnimatedBackground.vue';
import { useThemeStore } from '@/stores/theme';

export default {
  name: 'PageLayout',
  components: {
    AnimatedBackground
  },
  props: {
    title: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingText: {
      type: String,
      default: 'Chargement...'
    },
    theme: {
      type: String,
      default: 'auto'
    },
    maxWidth: {
      type: String,
      default: 'lg',
      validator: (value) => ['sm', 'md', 'lg', 'xl', 'full'].includes(value)
    },
    centered: {
      type: Boolean,
      default: false
    },
    showBack: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    themeClass() {
      if (this.theme && this.theme !== 'auto') {
        return `theme-${this.theme}`;
      }
      
      // Auto-detect based on user role
      const store = this.$store;
      if (store) {
        const role = store.getters.role;
        
        if (role === 'Veto') {
          return 'theme-medical';
        }
      }
      
      return 'theme-default';
    }
  },
  watch: {
    theme: {
      handler(newTheme) {
        this.updateGlobalTheme(newTheme);
      },
      immediate: true
    }
  },
  beforeUnmount() {
    // Reset to auto-detection when leaving the page
    const themeStore = useThemeStore();
    themeStore.clearOverride();
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    updateGlobalTheme(theme) {
      const themeStore = useThemeStore();
      if (theme && theme !== 'auto') {
        // Set explicit theme override
        themeStore.setTheme(theme);
      } else {
        // Use auto-detection based on role
        themeStore.clearOverride();
      }
    }
  }
};
</script>

<style scoped>
.glass-page {
  min-height: 100vh;
  position: relative;
}

.glass-page--centered {
  display: flex;
  align-items: center;
  justify-content: center;
}

.glass-page__content {
  position: relative;
  z-index: 1;
  width: 100%;
  margin: 0 auto;
  padding: 2rem;
  padding-top: 100px; /* Account for fixed navbar */
}

/* Max widths */
.glass-page__content--sm { max-width: 540px; }
.glass-page__content--md { max-width: 720px; }
.glass-page__content--lg { max-width: 960px; }
.glass-page__content--xl { max-width: 1140px; }
.glass-page__content--full { max-width: 100%; }

/* Centered layout adjustment */
.glass-page--centered .glass-page__content {
  padding-top: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100vh;
}

/* Header */
.glass-page__header {
  margin-bottom: 2rem;
}

.glass-page__header-content {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  color: #424242;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.7);
  transform: translateX(-4px);
}

.glass-page__title-section {
  flex: 1;
}

.glass-page__title {
  font-size: 2rem;
  font-weight: 700;
  color: #424242;
  margin: 0 0 0.25rem 0;
  line-height: 1.2;
}

.glass-page__subtitle {
  font-size: 1rem;
  color: rgba(66, 66, 66, 0.7);
  margin: 0;
}

.glass-page__actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

/* Main */
.glass-page__main {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Footer */
.glass-page__footer {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9998;
}

.loader-glass {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.loader-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid rgba(var(--theme-primary-rgb), 0.2);
  border-top-color: var(--theme-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loader-glass span {
  color: #424242;
  font-weight: 500;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .glass-page__content {
    padding: 1rem;
    padding-top: 80px;
  }
  
  .glass-page__title {
    font-size: 1.5rem;
  }
  
  .glass-page__header-content {
    flex-direction: column;
    gap: 1rem;
  }
  
  .glass-page__actions {
    width: 100%;
  }
}
</style>
